import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ILead, IDropdown } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVDropdownInput } from "../../shared/JVDropdownInput";
import { Tag } from "primereact/tag";
import { JVMultiselect } from "../../shared/JVMultiselect";

const schema = yup.object({
    contact: yup.object().shape({
        id: yup.number().nullable(),
        name: yup.string().nullable()
    }),
    statusId: yup.number().required("Status is required").min(1, "Status is required"),
    sourceId: yup.number().required("Source is required").min(1, "Source is required"),
    userId: yup.number().when('sourceId', {
        is: 3,
        then: schema => schema.required("Internal User is required").min(1, "Internal User is required"),
        otherwise: schema => schema.nullable(),
    }),
    partnerId: yup.number().when('sourceId', {
        is: 4,
        then: schema => schema.required("Partner is required").min(1, "Partner is required"),
        otherwise: schema => schema.nullable(),
    }),
    pocId: yup.number().when('sourceId', {
        is: 4,
        then: schema => schema.required("POC is required").min(1, "POC is required"),
        otherwise: schema => schema.nullable(),
    }),
    assignedTo: yup.number().nullable(),
});

interface Iprops {
    singleData: ILead,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Form = (props: Iprops) => {
    const [contacts, setContacts] = useState<IDropdown[]>();
    const [sources, setSources] = useState<IDropdown[]>();
    const [status, setStatus] = useState<IDropdown[]>();
    const [users, setUsers] = useState<IDropdown[]>();
    const [partners, setPartners] = useState<IDropdown[]>();
    const [poc, setPoc] = useState<IDropdown[]>();
    const [submitting, setSubmitting] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState<IDropdown[]>([]);
    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<ILead>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const sourceId = watch("sourceId");
    const partnerId = watch("partnerId");
    const contact = watch("contact");

    useEffect(() => {
        loadSources();
        loadPartners();
        loadUsers();
        loadStatus();
       // loadContacts(null);

    }, []);

    useEffect(() => {
        if (partnerId) loadPoc(partnerId);
        if (!props.singleData.id) setValue('pocId', undefined);
    }, [partnerId]);

    useEffect(() => {
        if (props.singleData.id) {
            props.singleData.contact = {
                id: props.singleData.contactId || 0,
                name: props.singleData.contactName + ' | ' + props.singleData.contactContact,
            };
            if (props.singleData.contact) {
                setSelectedContacts([props.singleData.contact]);
            }
        } else {
            setSelectedContacts([]);
        }
        reset(props.singleData);
    }, [props.singleData]);

    const loadPartners = async () => {
        const response = await Fetch("partner/dropdown", "GET", null, null, null);
        if (response) {
            setPartners(await response.json());
        }
    }

    const loadPoc = async (partnerId: number) => {
        const response = await Fetch(`partnerpoc/dropdown/${partnerId}`, "GET", null, null, null);
        if (response) {
            setPoc(await response.json());
        }
    }

    const loadSources = async () => {
        const response = await Fetch("source/dropdown", "GET", null, null, null);
        if (response) {
            setSources(await response.json());
        }
    }

    const loadUsers = async () => {
        const response = await Fetch("user/dropdown", "GET", null, null, null);
        if (response) {
            setUsers(await response.json());
        }
    }

    const loadContacts = async (text: string | null) => {
        const query = text ? `text=${text}` : '';
        const response = await Fetch("contact/dropdown", "GET", null, query, null);
        if (response) {
            const data = await response.json();
            setContacts(Array.isArray(data) ? data : (data?.contacts || []));
        }
    }
    const completeMethodContacts = (event: { query: string }) => {
        loadContacts(event.query);
    }

    const loadStatus = async () => {
        const response = await Fetch("status/dropdown", "GET", null, null, null);
        if (response) {
            setStatus(await response.json());
        }
    }

    const saveToDB = async (body: any) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        // body.contactId = body.contact?.id;
        // delete body.contact;

        // if (method === "POST") {
        //     body.contactIds = selectedContacts.map(c => c.id);
        // }

        if (method === "POST") {
            body.contactIds = selectedContacts.map((c) => c.id);
        } else {
            body.contactId = selectedContacts.length
                ? selectedContacts[0].id
                : null;
        }

        const response = await Fetch(`lead${props.singleData.id ? '/' + props.singleData.id : ''}`, method, body, null, props.toast);
        if (response) {
            onHide();
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.reloadData();
        }
        setSubmitting(false);
    }

    const onHide = () => {
        props.hideDialog();
        setSelectedContacts([]);

        reset({});
    }

    function onSubmit(data: ILead) {
        saveToDB(data);
    }

    function onContactConfirm() {
        if (!contact || !contact.id) return;
        if (selectedContacts.find(c => c.id === contact.id)) {
            props.toast.current.show({ severity: 'warn', detail: `${contact.name} already selected`, life: 3000 });
            setValue("contact", undefined);
            return;
        }
        setSelectedContacts([...selectedContacts, contact]);
        setValue("contact", undefined);
    }

    function removeContact(contactId: number) {
        setSelectedContacts(selectedContacts.filter(c => c.id !== contactId));
    }


    const mergedContacts = [...(selectedContacts || [])];
    (contacts || []).forEach(c => {
        if (!mergedContacts.some(mc => mc.id === c.id)) {
            mergedContacts.push(c);
        }
    });

    return (
        <Dialog visible={props.open} style={{ width: '400px' }} header="Add/Edit Lead" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                {selectedContacts.map(cont => {
                    return <div key={cont.id} className="field col-12"><Tag className="mr-2 py-2 px-3" severity="warning" icon="pi pi-user" value={cont.name}> <i onClick={() => removeContact(cont.id)} className="pi pi-times text-xs d-block ml-3 cursor-pointer"></i></Tag></div>
                })}
                {/* <JVDropdownInput column={1} func={contact ? onContactConfirm : undefined} disabled={props.singleData.id ? true : false} completeMethod={completeMethodContacts} control={control} name="contact" error={errors.contact?.id?.message} options={contacts} optionLabel="name" placeholder="Search Contact" /> */}
                <JVMultiselect
                    column={1}
                    label="Contacts"
                    required
                    options={mergedContacts}
                    optionLabel="name"
                    value={selectedContacts}
                    placeholder="Select Contacts"
                    // disabled={props.singleData.id}
                    onChange={(value) => setSelectedContacts(value)}
                    filter
                    onFilter={(e: any) => loadContacts(e.filter)}
                    dataKey="id"
                />
                <JVDropdown column={1} required label="Source" control={control} options={sources} optionLabel="name" optionValue="id" name="sourceId" placeholder="Select Source" error={errors.sourceId?.message} />
                {sourceId === 3 && <JVDropdown column={1} required label="Internal User" control={control} options={users} optionLabel="name" optionValue="id" name="userId" placeholder="Select Internal User" error={errors.userId?.message} />}
                {sourceId === 4 && <JVDropdown column={1} required label="Partner" control={control} options={partners} optionLabel="name" optionValue="id" name="partnerId" placeholder="Select Partner" error={errors.partnerId?.message} />}
                {sourceId === 4 && <JVDropdown column={1} required label="POC" control={control} options={poc} optionLabel="name" optionValue="id" name="pocId" placeholder="Select POC" error={errors.pocId?.message} />}
                <JVDropdown column={1} label="Assign To" control={control} options={users} optionLabel="name" optionValue="id" name="assignedTo" placeholder="Select" error={errors.assignedTo?.message} />
                <JVDropdown column={1} required label="Status" control={control} options={status} optionLabel="name" optionValue="id" name="statusId" placeholder="Select Status" error={errors.statusId?.message} />
                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}